import React, { useState, useEffect, useRef } from "react";
import { sendChatMessage, getChatHistory } from "../../services/api";
import ChatMessage from "./ChatMessage";
import { ChatMessage as ChatMessageType } from "../../services/api";

const ChatbotWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [sessionId, setSessionId] = useState<string>("");
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const lastSpokenMessageIdRef = useRef<string | null>(null);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any | null>(null);

  // Load session ID from localStorage or create a new conversation
  useEffect(() => {
    const storedSessionId = localStorage.getItem("chatSessionId");
    if (storedSessionId) {
      setSessionId(storedSessionId);
      loadChatHistory(storedSessionId);
    }
  }, []);

  // Scroll to bottom of messages when new ones are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // TTS for bot messages
  useEffect(() => {
    if (!isOpen) {
      // Only speak if widget is open
      if (
        window.speechSynthesis &&
        (window.speechSynthesis.speaking || window.speechSynthesis.pending)
      ) {
        window.speechSynthesis.cancel();
      }
      if (isSpeaking) setIsSpeaking(false);
      return;
    }

    if (!("speechSynthesis" in window)) {
      console.warn("Browser does not support SpeechSynthesis.");
      return;
    }

    const latestMessage =
      messages.length > 0 ? messages[messages.length - 1] : null;

    if (
      latestMessage &&
      !latestMessage.isUserMessage &&
      latestMessage._id !== lastSpokenMessageIdRef.current
    ) {
      // Cancel any previous speech
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(latestMessage.content);
      utterance.onstart = () => {
        setIsSpeaking(true);
        lastSpokenMessageIdRef.current = latestMessage._id; // Mark as spoken
      };
      utterance.onend = () => {
        setIsSpeaking(false);
      };
      utterance.onerror = (event) => {
        console.error("SpeechSynthesisUtterance.onerror", event);
        setIsSpeaking(false);
      };

      window.speechSynthesis.speak(utterance);
    } else if (!latestMessage || latestMessage.isUserMessage) {
      if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
        window.speechSynthesis.cancel();
      }
      if (isSpeaking) setIsSpeaking(false);
    }

    // Cleanup: cancel speech if component unmounts or is closed while speaking
    return () => {
      if (window.speechSynthesis.speaking) {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
      }
    };
  }, [messages, isOpen]); // Rerun when messages change OR when widget is opened/closed

  // Load chat history for existing sessions
  const loadChatHistory = async (sid: string) => {
    try {
      setIsLoading(true);
      const response = await getChatHistory(sid);
      setMessages(response.messages);
      setIsLoading(false);
    } catch (error) {
      console.error("Error loading chat history:", error);
      setIsLoading(false);
    }
  };

  // Actual message sending logic
  const sendUserMessageToServer = async (messageContent: string) => {
    if (!messageContent.trim()) return;

    const userMessage: ChatMessageType = {
      _id: `temp-${Date.now()}`,
      sessionId: sessionId || "temp-session",
      isUserMessage: true,
      content: messageContent,
      timestamp: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setIsLoading(true);

    try {
      const response = await sendChatMessage(messageContent, sessionId);
      if (!sessionId) {
        setSessionId(response.sessionId);
        localStorage.setItem("chatSessionId", response.sessionId);
      }
      const botMessage: ChatMessageType = {
        _id: `bot-${Date.now()}`,
        sessionId: response.sessionId,
        isUserMessage: false,
        content: response.botResponse,
        timestamp: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setMessages((prevMessages) => [...prevMessages, botMessage]);
    } catch (error) {
      console.error("Error sending message:", error);
      const errorMessage: ChatMessageType = {
        _id: `error-${Date.now()}`,
        sessionId: sessionId || "temp-session",
        isUserMessage: false,
        content:
          "Sorry, I'm having trouble connecting. Please try again later.",
        timestamp: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setMessages((prevMessages) => [...prevMessages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle sending messages from form
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;
    await sendUserMessageToServer(inputMessage);
    setInputMessage(""); // Clear input after form submission
  };

  // Handle STT
  const handleToggleListen = () => {
    if (!isOpen) return; // Don't do STT if widget is closed

    const SpeechRecognitionAPI =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) {
      console.error("Speech Recognition API not supported.");
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    recognitionRef.current = new SpeechRecognitionAPI();
    const recognition = recognitionRef.current;
    if (!recognition) return;

    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = "en-US";

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = async (event: any) => {
      const transcript = event.results[0][0].transcript;
      setInputMessage(transcript); // Update input field for visual feedback
      await sendUserMessageToServer(transcript); // Send the transcript directly
      setInputMessage(""); // Clear input after sending
    };

    recognition.onerror = (event: any) => {
      console.error("STT Error:", event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  };

  // Toggle chat window
  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Chat Button */}
      <button
        onClick={toggleChat}
        className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg flex items-center justify-center"
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        {isOpen ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
            />
          </svg>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-80 md:w-96 bg-white rounded-lg shadow-xl overflow-hidden border border-gray-200">
          {/* Chat Header */}
          <div className="bg-blue-600 text-white px-4 py-3 flex items-center justify-between">
            <h3 className="font-medium">Chat Assistant</h3>
            <div className="flex items-center">
              {isSpeaking && (
                <button
                  onClick={() => {
                    window.speechSynthesis.cancel();
                    setIsSpeaking(false);
                  }}
                  className="text-sm bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded mr-2"
                  aria-label="Stop speaking"
                >
                  Stop
                </button>
              )}
              <button
                onClick={toggleChat}
                className="text-white hover:text-gray-200"
                aria-label="Close chat"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Chat Messages */}
          <div className="h-80 overflow-y-auto p-4 bg-gray-50">
            {messages.length === 0 ? (
              <div className="text-center text-gray-500 mt-10">
                <p>Welcome! How can I help you today?</p>
              </div>
            ) : (
              messages.map((msg) => (
                <ChatMessage
                  key={msg._id}
                  content={msg.content}
                  isUserMessage={msg.isUserMessage}
                  timestamp={msg.timestamp}
                />
              ))
            )}
            {isLoading && (
              <div className="flex justify-start mb-4">
                <div className="bg-gray-200 text-gray-800 rounded-lg rounded-bl-none px-4 py-2">
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-2 h-2 bg-gray-600 rounded-full animate-bounce"
                      style={{ animationDelay: "0ms" }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-gray-600 rounded-full animate-bounce"
                      style={{ animationDelay: "150ms" }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-gray-600 rounded-full animate-bounce"
                      style={{ animationDelay: "300ms" }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Chat Input */}
          <form onSubmit={handleSendMessage} className="p-3 border-t">
            <div className="flex items-center">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 px-3 py-2 border rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading || isListening}
              />
              <button
                type="button"
                onClick={handleToggleListen}
                className={`p-2 border-y ${
                  isListening
                    ? "bg-red-500 hover:bg-red-600"
                    : "bg-gray-200 hover:bg-gray-300"
                } text-gray-700 flex items-center justify-center`}
                title={isListening ? "Stop listening" : "Start listening"}
                disabled={isLoading}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {isListening ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v3a3 3 0 01-3 3z"
                    />
                  )}
                </svg>
              </button>
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-r-lg"
                disabled={isLoading}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatbotWidget;
